import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Flex } from 'grid-styled';
import { Customer, Price, Service, Time } from '@/types';
import classNames from 'classnames/bind';
import { formattedFromTo, parseIntervalKey, stringifyIntervalKey } from '@/utils/common';
import styles from './NewBookingForm.module.css';
import Form from '@/components/Form';
import Counter from '@/components/Counter';
import { useAppDispatch, useAppSelector } from '@/hooks';
import {
    calculatePrice,
    calculatePrices,
    getTotalQuantityForDate,
    removeCalculatedPrice,
    ARTICLE_ONLY_PRICE_ID,
    CalculatePriceArticle,
} from '@/store/reducers/calculatePrice';
import {
    selectQuantity,
    selectAllQuantities,
    setQuantity,
    resetQuantities,
    getIntervalKey,
    parseSliceIntervalKey,
} from '@/store/reducers/dateQuantitiesForm';
import { getValidPriceIds } from '@/utils/priceValidity';
import { getPricesInfo } from '../components/common';
import { DateQuantityForm } from './DateQuantityForm';
import { ApplyRebateCodes } from './ApplyRebateCodes';
import useTranslate from '@/hooks/useTranslate';
import { Loader } from 'semantic-ui-react';
import { debounce, isEqual } from 'lodash';

export let cx = classNames.bind(styles);

export const RECALCULATE_PRICE_DEBOUNCE_TIMEOUT = 1000;

/** Stable identity so an absent article list never re-triggers the price effect. */
const EMPTY_ARTICLES: CalculatePriceArticle[] = [];

const noopSubmit = () => () => { };

export interface DateQuantityProps {
    timeSpot: Time;
    prices: Price[];
    service: Service;
    customer: Customer;
    /** Ids of the prices valid for THIS occurrence's own date/time/weekday. */
    validPriceIds: Set<number>;
}

interface DateQuantitiesProps {
    timeSpots: Time[];
    prices: Price[];
    service: Service;
    customer: Customer;
    /**
     * Rendered between the quantity rows and the total. The service-article picker
     * goes here so add-ons read as part of the same group as the lines they add
     * to, and sit above the total that already includes them.
     */
    articlesSlot?: React.ReactNode;
}

export const DATE_QUANTITIES_PREFIX = 'DateQuantities_';

// ─── Virtualisation constants ────────────────────────────────────────────────

const VIRTUAL_ESTIMATED_HEIGHT = 52;
const VIRTUAL_ITEM_GAP = 2;
const VIRTUAL_MAX_HEIGHT = 600;
const VIRTUAL_OVERSCAN = 10;
const VIRTUALIZE_THRESHOLD = 100;

// ─── Helpers ─────────────────────────────────────────────────────────────────

type EffectData = Record<
    string,
    { interval: { From: string; To: string }; prices: { priceId: number; quantity: number }[] }
>;

/**
 * Merge quantities from the new slice (group path) and legacy redux-form
 * entries (non-group path) into one structure the price-calculation effect can
 * consume.
 */
function computeEffectData(
    sliceQuantities: Record<string, Record<number, number>>,
    formQuantities: any[]
): EffectData {
    const result: EffectData = {};

    // From new slice (group path) — keyed by "from|to"
    Object.entries(sliceQuantities).forEach(([intervalKey, pricesMap]) => {
        const { From, To } = parseSliceIntervalKey(intervalKey);
        result[intervalKey] = {
            interval: { From, To },
            prices: Object.entries(pricesMap).map(([priceId, quantity]) => ({
                priceId: Number(priceId),
                quantity: quantity as number,
            })),
        };
    });

    // From redux-form (non-group path)
    formQuantities.forEach(([interval, values]) => {
        const fromMs = new Date(interval.From).getTime();
        const toMs = new Date(interval.To).getTime();
        if (Number.isNaN(fromMs) || Number.isNaN(toMs)) return;
        const key = `${new Date(interval.From).toISOString()}|${new Date(interval.To).toISOString()}`;
        if (!result[key]) {
            result[key] = { interval: { From: interval.From, To: interval.To }, prices: [] };
        }
        if (values?.price != null && values?.quantity > 0) {
            result[key].prices.push({
                priceId: Number(values.price),
                quantity: Number(values.quantity),
            });
        }
    });

    return result;
}

/** Custom equality for the legacy redux-form selector (non-group path only). */
function dateQuantitiesEqual(a: any[], b: any[]): boolean {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
        if (a[i][2] !== b[i][2]) return false;
        if (a[i][1]?.price !== b[i][1]?.price) return false;
        if (a[i][1]?.quantity !== b[i][1]?.quantity) return false;
    }
    return true;
}

// ─── DateQuantities (parent orchestrator) ────────────────────────────────────

export const DateQuantities: React.FC<DateQuantitiesProps> = ({
    timeSpots,
    prices,
    service,
    customer,
    articlesSlot,
}) => {
    const dispatch = useAppDispatch();
    const priceInfo = useMemo(() => getPricesInfo(service), [service]);
    const { hasPrices } = priceInfo;

    // Determine rendering strategy
    const isGroupService =
        hasPrices && (priceInfo.hasGroupBooking || priceInfo.hasMultipleResource);
    const totalGroupItems = isGroupService ? timeSpots.length * prices.length : 0;
    const useVirtualized = totalGroupItems > VIRTUALIZE_THRESHOLD;

    // ── Slice-based quantities (group path) ──────────────────────────────────
    const sliceQuantities = useAppSelector(selectAllQuantities);

    // ── Form-based quantities (non-group path) ──────────────────────────────
    const formQuantities = useAppSelector((state: any) => {
        if (useVirtualized) return [];
        return Object.entries((state?.form || {}) as any).reduce((acc: any[], curr: any) => {
            const formKey = curr[0];
            if (formKey.includes(DATE_QUANTITIES_PREFIX)) {
                const interval = parseIntervalKey(formKey, DATE_QUANTITIES_PREFIX);
                acc.push([interval, curr[1]?.values, formKey]);
            }
            return acc;
        }, [] as any[]);
    }, dateQuantitiesEqual);

    // ── Combined effect data ─────────────────────────────────────────────────
    const effectData = useMemo(
        () => computeEffectData(sliceQuantities, formQuantities),
        [sliceQuantities, formQuantities]
    );

    // ── Per-occurrence price validity ────────────────────────────────────────
    // `prices` is the union over the whole series (see fetchServiceWithPrice);
    // each occurrence only gets to book the categories valid on its OWN date.
    const validPriceIdsByInterval = useMemo(() => {
        const map = new Map<string, Set<number>>();
        timeSpots.forEach((t) => map.set(getIntervalKey(t), getValidPriceIds(prices, t.From as string)));
        return map;
    }, [timeSpots, prices]);

    // Drop quantities the admin set before the valid set changed (e.g. dates edited
    // afterwards), so totals and the submitted payload can never carry an
    // out-of-window PriceId. Converges: the write zeroes the offending entry, which
    // fails the `quantity > 0` predicate on the next pass.
    useEffect(() => {
        Object.entries(sliceQuantities).forEach(([intervalKey, pricesMap]) => {
            const valid = validPriceIdsByInterval.get(intervalKey);
            if (!valid) return;
            Object.entries(pricesMap).forEach(([priceId, quantity]) => {
                if (quantity > 0 && !valid.has(Number(priceId))) {
                    dispatch(setQuantity({ intervalKey, priceId: Number(priceId), quantity: 0 }));
                }
            });
        });
    }, [validPriceIdsByInterval, sliceQuantities, dispatch]);

    // The add-on picker writes {ArticleId, Quantity} rows straight into the
    // booking form; the quoted total is wrong without them.
    const articles = useAppSelector(
        (s: any) => (s.form?.booking?.values?.Articles as CalculatePriceArticle[]) ?? EMPTY_ARTICLES,
        isEqual
    );

    const prevDataRef = useRef<EffectData | null>(null);
    const prevEmailRef = useRef<string | null>(null);
    const prevArticlesRef = useRef<CalculatePriceArticle[] | null>(null);

    const effect = useCallback(
        (currentData: EffectData, customerEmail: string) => {
            const prevData = prevDataRef.current;
            const prevEmail = prevEmailRef.current;

            const allKeys = new Set([
                ...Object.keys(currentData),
                ...Object.keys(prevData || {}),
            ]);

            const requests: any[] = [];
            const removals: any[] = [];

            allKeys.forEach((key) => {
                const prev = prevData?.[key]?.prices || [];
                const curr = currentData[key]?.prices || [];
                const interval =
                    currentData[key]?.interval || prevData?.[key]?.interval;

                const changed =
                    !isEqual(prev, curr) ||
                    prevEmail !== customerEmail ||
                    // Articles are attached per booking, so an article edit must
                    // re-quote EVERY interval, not just the ones whose quantities moved.
                    !isEqual(prevArticlesRef.current, articles);
                if (!changed || !interval) return;

                const currPriceSet = new Set(
                    curr.filter((c) => c.quantity > 0).map((c) => c.priceId)
                );

                prev.forEach((p) => {
                    if (!currPriceSet.has(p.priceId)) {
                        removals.push({ PriceId: p.priceId, Interval: interval });
                    }
                });

                // Never send an out-of-window PriceId to calculateprice — the backend
                // silently drops it from the total (ServiceExtensions.cs:331), so the
                // admin would see a subtotal missing a line they can see a counter for.
                const valid = validPriceIdsByInterval.get(key);
                const validQuantities = curr
                    .filter((c) => c.quantity > 0 && (!valid || valid.has(c.priceId)))
                    .map((c) => ({ Quantity: c.quantity, PriceId: c.priceId }));

                // Add-ons are charged per booking whether or not any spots are
                // booked, so a basket with add-ons and no quantities still has to
                // be quoted. Without this the admin saw "0 kr" — or no total at
                // all — while the created booking was charged for every add-on.
                //
                // `Quantities: []` is accepted for a plain service (one price, no
                // group booking, no multiple resources); on a group or multi-price
                // service it is rejected, which now surfaces an error and disables
                // submit rather than under-quoting.
                if (validQuantities.length > 0 || articles.length > 0) {
                    requests.push({
                        serviceId: service.Id,
                        interval,
                        quantities: validQuantities,
                        customerEmail,
                        rebateCodeIds: [],
                        articles,
                    });
                } else {
                    // Nothing left to price for this interval. An add-on-only quote
                    // is keyed on a sentinel PriceId, so drop it explicitly or its
                    // total would outlive the add-ons that produced it.
                    removals.push({ PriceId: ARTICLE_ONLY_PRICE_ID, Interval: interval });
                }
            });

            removals.forEach((r) => dispatch(removeCalculatedPrice(r)));
            if (requests.length === 1) {
                dispatch(calculatePrice(requests[0]));
            } else if (requests.length > 1) {
                dispatch(calculatePrices(requests));
            }

            prevDataRef.current = currentData;
            prevEmailRef.current = customerEmail;
            prevArticlesRef.current = articles;
        },
        // `articles` here is what makes an article edit re-quote: a new identity
        // changes `effect`, which re-runs the driving effect below. Adding it to
        // that effect's own dep array too would be redundant.
        [dispatch, service.Id, validPriceIdsByInterval, articles]
    );

    const debouncedEffect = useMemo(
        () => debounce(effect, RECALCULATE_PRICE_DEBOUNCE_TIMEOUT),
        [effect]
    );

    const loading = useAppSelector((s) => s.calculatePrice.loading);

    useEffect(() => {
        debouncedEffect.cancel();
        effect(effectData, customer.Email);
    }, [effectData, effect]);

    useEffect(() => {
        debouncedEffect(effectData, customer.Email);
        return () => debouncedEffect.cancel();
    }, [customer.Email, debouncedEffect]);

    useEffect(() => () => debouncedEffect.cancel(), [debouncedEffect]);

    // Reset slice quantities on unmount
    useEffect(() => () => { dispatch(resetQuantities()); }, [dispatch]);

    const rebateQuantities = useMemo(
        () =>
            Object.values(effectData)
                .flatMap((d) => d.prices)
                .filter((p) => p.quantity > 0)
                .map((p) => ({ Quantity: p.quantity, PriceId: p.priceId })),
        [effectData]
    );

    return (
        <Box className={cx('DateQuantities')} disabled={loading}>
            {loading ? <Loader /> : null}
            {useVirtualized ? (
                <VirtualizedGroupPrices
                    timeSpots={timeSpots}
                    prices={prices}
                    service={service}
                    validPriceIdsByInterval={validPriceIdsByInterval}
                />
            ) : (
                timeSpots.map((time) => (
                    <DateQuantity
                        key={`${time.From}_${time.To}`}
                        timeSpot={time}
                        prices={prices}
                        service={service}
                        customer={customer}
                        validPriceIds={
                            validPriceIdsByInterval.get(getIntervalKey(time)) ?? new Set<number>()
                        }
                    />
                ))
            )}
            {articlesSlot}
            {hasPrices ? (
                <DateQuantitiesSubtotal priceSign={prices[0]?.PriceSign} />
            ) : null}
            {timeSpots.length === 1 && hasPrices ? (
                <ApplyRebateCodes
                    service={service}
                    quantities={rebateQuantities}
                    articles={articles}
                    timeSpot={timeSpots[0]}
                    customer={customer}
                />
            ) : null}
        </Box>
    );
};

// ─── VirtualList (auto-measuring) ────────────────────────────────────────────

interface VirtualListProps {
    itemCount: number;
    estimatedItemHeight: number;
    maxHeight: number;
    overscan: number;
    renderItem: (index: number) => React.ReactNode;
    getItemKey: (index: number) => string;
}

/** Binary-search: find the first item whose bottom edge is past `scrollTop`. */
function findStartIndex(offsets: number[], scrollTop: number): number {
    let lo = 0;
    let hi = offsets.length - 2;
    while (lo < hi) {
        const mid = (lo + hi) >>> 1;
        if (offsets[mid + 1] <= scrollTop) {
            lo = mid + 1;
        } else {
            hi = mid;
        }
    }
    return lo;
}

/** Wrapper that measures its content via ResizeObserver. */
const MeasuredItem = React.memo<{
    index: number;
    top: number;
    reportHeight: (index: number, height: number) => void;
    children: React.ReactNode;
}>(({ index, top, reportHeight, children }) => {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const ro = new ResizeObserver(() => {
            reportHeight(index, el.offsetHeight);
        });
        ro.observe(el);
        reportHeight(index, el.offsetHeight);
        return () => ro.disconnect();
    }, [index, reportHeight]);

    return (
        <div
            ref={ref}
            style={{
                position: 'absolute',
                top,
                left: 0,
                right: 0,
                display: 'flex',
                alignItems: 'center',
            }}
        >
            {children}
        </div>
    );
});

const VirtualList: React.FC<VirtualListProps> = ({
    itemCount,
    estimatedItemHeight,
    maxHeight,
    overscan,
    renderItem,
    getItemKey,
}) => {
    const [scrollTop, setScrollTop] = useState(0);
    const [revision, setRevision] = useState(0);
    const heightsRef = useRef<Map<number, number>>(new Map());
    const rafRef = useRef(0);

    const reportHeight = useCallback((index: number, height: number) => {
        const prev = heightsRef.current.get(index);
        if (prev !== undefined && Math.abs(prev - height) < 1) return;
        heightsRef.current.set(index, height);
        if (!rafRef.current) {
            rafRef.current = requestAnimationFrame(() => {
                rafRef.current = 0;
                setRevision((r) => r + 1);
            });
        }
    }, []);

    useEffect(
        () => () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        },
        []
    );

    // Recompute offsets whenever measurements change
    const offsets = useMemo(() => {
        void revision;
        const arr = new Array(itemCount + 1);
        arr[0] = 0;
        for (let i = 0; i < itemCount; i++) {
            arr[i + 1] = arr[i] + (heightsRef.current.get(i) ?? estimatedItemHeight) + VIRTUAL_ITEM_GAP;
        }
        return arr;
    }, [itemCount, estimatedItemHeight, revision]);

    const totalHeight = offsets[itemCount] || 0;
    const containerHeight = Math.min(maxHeight, totalHeight);

    const rawStart = findStartIndex(offsets, scrollTop);
    const startIndex = Math.max(0, rawStart - overscan);

    const visibleBottom = scrollTop + containerHeight;
    let rawEnd = rawStart;
    while (rawEnd < itemCount && offsets[rawEnd] < visibleBottom) {
        rawEnd++;
    }
    const endIndex = Math.min(itemCount, rawEnd + overscan);

    const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
        setScrollTop(e.currentTarget.scrollTop);
    }, []);

    const items = [];
    for (let i = startIndex; i < endIndex; i++) {
        items.push(
            <MeasuredItem
                key={getItemKey(i)}
                index={i}
                top={offsets[i]}
                reportHeight={reportHeight}
            >
                {renderItem(i)}
            </MeasuredItem>
        );
    }

    return (
        <div
            className={cx('virtual-list')}
            style={{ height: containerHeight, overflow: 'auto', position: 'relative' }}
            onScroll={handleScroll}
        >
            <div style={{ height: totalHeight, position: 'relative' }}>{items}</div>
        </div>
    );
};

// ─── VirtualizedGroupPrices ──────────────────────────────────────────────────

type FlatItem =
    | { type: 'header'; timeSpot: Time; intervalKey: string }
    | { type: 'priceRow'; timeSpot: Time; price: Price; intervalKey: string };

const VirtualizedGroupPrices = React.memo<{
    timeSpots: Time[];
    prices: Price[];
    service: Service;
    validPriceIdsByInterval: Map<string, Set<number>>;
}>(({ timeSpots, prices, service, validPriceIdsByInterval }) => {
    const flatItems = useMemo(() => {
        const items: FlatItem[] = [];
        timeSpots.forEach((timeSpot) => {
            const intervalKey = getIntervalKey(timeSpot);
            items.push({ type: 'header', timeSpot, intervalKey });
            prices.forEach((price) => {
                items.push({ type: 'priceRow', timeSpot, price, intervalKey });
            });
        });
        return items;
    }, [timeSpots, prices]);

    const priceInfo = useMemo(() => getPricesInfo(service), [service]);

    const renderItem = useCallback(
        (index: number) => {
            const item = flatItems[index];
            if (item.type === 'header') {
                return (
                    <Box className={cx('virtual-header')} style={{ width: '100%' }}>
                        {formattedFromTo(item.timeSpot.From, item.timeSpot.To)}
                    </Box>
                );
            }
            const maxQuantity =
                !priceInfo.hasGroupBooking && !priceInfo.hasMultipleResource
                    ? 1
                    // `Free` and `FreeSpots` are one server-side number under two
                    // names; `Free` is the canonical one.
                    : item.timeSpot.Free ?? item.timeSpot.FreeSpots;
            return (
                <PriceRow
                    price={item.price}
                    intervalKey={item.intervalKey}
                    maxQuantity={maxQuantity}
                    disabled={!validPriceIdsByInterval.get(item.intervalKey)?.has(item.price.Id)}
                />
            );
        },
        [flatItems, priceInfo, validPriceIdsByInterval]
    );

    const getItemKey = useCallback(
        (index: number) => {
            const item = flatItems[index];
            if (item.type === 'header') return `h|${item.intervalKey}`;
            return `r|${item.intervalKey}|${item.price.Id}`;
        },
        [flatItems]
    );

    return (
        <VirtualList
            itemCount={flatItems.length}
            estimatedItemHeight={VIRTUAL_ESTIMATED_HEIGHT}
            maxHeight={VIRTUAL_MAX_HEIGHT}
            overscan={VIRTUAL_OVERSCAN}
            renderItem={renderItem}
            getItemKey={getItemKey}
        />
    );
});

// ─── PriceRow (lightweight — no redux-form) ──────────────────────────────────

interface PriceRowProps {
    price: Price;
    intervalKey: string;
    maxQuantity: number;
    /** True when this price is not valid for this occurrence's own date/time/weekday. */
    disabled?: boolean;
}

const PriceRow = React.memo<PriceRowProps>(({ price, intervalKey, maxQuantity, disabled }) => {
    const dispatch = useAppDispatch();
    const { translate } = useTranslate();
    const quantity = useAppSelector((s) => selectQuantity(s, intervalKey, price.Id));

    const quantityRef = useRef(quantity);
    quantityRef.current = quantity;

    const handleIncrement = useCallback(() => {
        if (disabled) return; // stale render must not dispatch for an invalid price
        dispatch(
            setQuantity({
                intervalKey,
                priceId: price.Id,
                quantity: quantityRef.current + 1,
                maxQuantity,
            })
        );
    }, [dispatch, intervalKey, price.Id, maxQuantity, disabled]);

    const handleDecrement = useCallback(() => {
        if (disabled) return;
        dispatch(
            setQuantity({
                intervalKey,
                priceId: price.Id,
                quantity: Math.max(0, quantityRef.current - 1),
            })
        );
    }, [dispatch, intervalKey, price.Id, disabled]);

    const rowPrice = price.Price * quantity;

    return (
        <Box className={cx('row', disabled && 'row-disabled')} style={{ width: '100%' }}>
            <Box className={cx('left', 'small')}>
                <Box className={cx('plain-price-text')}>
                    {price.PriceText}
                    {disabled ? (
                        <Box className={cx('price-unavailable')}>
                            {translate('newBookingForm.prices.notAvailableForTime')}
                        </Box>
                    ) : null}
                </Box>
                <Box className={cx('counter-wrapper')}>
                    <Flex
                        justifyContent="center"
                        className={cx('counter')}
                        data-testid={`counter-${price.Id}`}
                    >
                        <Counter
                            value={quantity}
                            min={0}
                            disabled={disabled}
                            onDecrement={handleDecrement}
                            onIncrement={handleIncrement}
                        />
                    </Flex>
                </Box>
            </Box>
            <Box className={cx('right')}>
                {rowPrice}
                {price.PriceSign}
            </Box>
        </Box>
    );
});

// ─── DateQuantity ────────────────────────────────────────────────────────────

export const DateQuantity = React.memo<DateQuantityProps>(({ timeSpot, prices, service, validPriceIds }) => {
    const priceInfo = useMemo(() => getPricesInfo(service), [service]);

    // `Free` and `FreeSpots` are one server-side number under two names; `Free` is
    // the canonical one, so a slot carrying either is capped the same way.
    const freeSpots = timeSpot.Free ?? timeSpot.FreeSpots;

    const isQueueOverflow = !!service?.EnableBookingQueue && (freeSpots ?? 0) === 0;

    const groupMax = isQueueOverflow
        ? (service?.MaxNumberOfSpotsPerBooking ?? freeSpots)
        : freeSpots;

    const maxQuantity =
        !priceInfo.hasGroupBooking && !priceInfo.hasMultipleResource ? 1 : groupMax;
    const hasMultiplePricesNonGroup = priceInfo.hasMultiplePrices && !priceInfo.hasGroupBooking;

    const isNonGroupPath =
        !priceInfo.hasPrices ||
        hasMultiplePricesNonGroup ||
        (!priceInfo.hasGroupBooking && !priceInfo.hasMultipleResource) ||
        maxQuantity === 1;

    const intervalKey = useMemo(() => getIntervalKey(timeSpot), [timeSpot]);

    const totalQuantity = useAppSelector((s) =>
        isNonGroupPath
            ? getTotalQuantityForDate(s, timeSpot.From as string, timeSpot.To as string)
            : 0
    );
    const totalQuantityRef = useRef(totalQuantity);
    totalQuantityRef.current = totalQuantity;

    const onIncrement = useCallback(
        (val: number, cb: () => void) => {
            if (totalQuantityRef.current < maxQuantity || typeof maxQuantity === 'undefined') {
                cb();
            }
        },
        [maxQuantity]
    );
    const onDecrement = useCallback((_val: number, cb: () => void) => {
        cb();
    }, []);

    const nonGroupFormKey = useMemo(
        () => stringifyIntervalKey(timeSpot, DATE_QUANTITIES_PREFIX),
        [timeSpot]
    );
    // Seed the first VALID price for this occurrence, not blindly prices[0] —
    // prices is the union over the whole series.
    const firstValidPrice = useMemo(
        () => prices.find((p) => validPriceIds.has(p.Id)) ?? prices[0],
        [prices, validPriceIds]
    );
    const nonGroupInitialValues = useMemo(
        () => ({
            quantity: totalQuantity > 0
                ? (typeof maxQuantity === 'number' ? Math.min(totalQuantity, maxQuantity) : totalQuantity)
                : ((typeof maxQuantity === 'undefined' || maxQuantity >= 1) ? 1 : 0),
            ...(priceInfo.hasPrices ? { price: firstValidPrice.Id } : {}),
        }),
        [maxQuantity, priceInfo.hasPrices, firstValidPrice, totalQuantity]
    );

    if (isNonGroupPath) {
        return (
            <Box className={cx('DateQuantity')}>
                <Box>{formattedFromTo(timeSpot.From, timeSpot.To)}</Box>
                <Form
                    key={nonGroupFormKey}
                    name={nonGroupFormKey}
                    initialValues={nonGroupInitialValues}
                    onSubmit={noopSubmit}
                    showActionBar={false}
                    showSuccessMesage={false}
                >
                    {(formProps: any) => (
                        <DateQuantityForm
                            formProps={formProps}
                            timeSpot={timeSpot}
                            prices={prices}
                            validPriceIds={validPriceIds}
                            maxQuantity={maxQuantity}
                            service={service}
                            onIncrement={onIncrement}
                            onDecrement={onDecrement}
                            {...formProps}
                        />
                    )}
                </Form>
            </Box>
        );
    }

    // Group path (small number — not virtualised)
    return (
        <Box className={cx('DateQuantity')}>
            <Box>{formattedFromTo(timeSpot.From, timeSpot.To)}</Box>
            {prices.map((price) => (
                <PriceRow
                    key={price.Id}
                    price={price}
                    intervalKey={intervalKey}
                    maxQuantity={maxQuantity}
                    disabled={!validPriceIds.has(price.Id)}
                />
            ))}
        </Box>
    );
});

// ─── DateQuantitiesSubtotal ──────────────────────────────────────────────────

export const DateQuantitiesSubtotal: React.FC<{ priceSign: string }> = ({ priceSign }) => {
    const calculatedPrice = useAppSelector((state) => state.calculatePrice);
    const { translate } = useTranslate();

    if (!calculatedPrice.entity?.calculatedPrice) {
        return null;
    }

    const appliedCodes =
        calculatedPrice.entity.appliedCodes?.length === 1 &&
            calculatedPrice.entity.appliedCodes[0].AppliedCodes
            ? calculatedPrice.entity.appliedCodes[0].AppliedCodes
            : [];

    const totalPrice =
        appliedCodes.length === 0
            ? calculatedPrice.entity.calculatedPrice.reduce((acc: number, curr: any) => {
                acc = acc + curr.TotalPrice;
                return acc;
            }, 0)
            : // @ts-ignore
            calculatedPrice.entity.appliedCodes[0].TotalPrice;

    return (
        <Box className={cx('DateQuantitiesSubtotal')}>
            {calculatedPrice.entity.appliedCodes && appliedCodes.length > 0 ? (
                <Box style={{ display: 'grid', gap: '4px' }}>
                    <Flex className={cx('appliedCode')}>
                        <Box className={cx('sign')}>
                            {translate('newBookingForm.appliedCode', {
                                sign: appliedCodes[0].RebateCodeSign,
                            })}
                        </Box>
                        <Box
                            className={cx('rebatePrice')}
                            color={'var(--positive-text-color)'}
                        >
                            - {appliedCodes[0].RebateAmount}
                            {priceSign}
                        </Box>
                    </Flex>
                    <Flex className={cx('total-price')}>
                        <Box>{translate('newBookingForm.prices.total')}</Box>
                        <Box className={cx('right')}>
                            <strong>
                                {totalPrice}
                                {priceSign}
                            </strong>
                        </Box>
                    </Flex>
                </Box>
            ) : (
                <Flex className={cx('total-price')}>
                    <Box>{translate('newBookingForm.prices.total')}</Box>
                    <Box className={cx('right')}>
                        <strong>
                            {totalPrice}
                            {priceSign}
                        </strong>
                    </Box>
                </Flex>
            )}
        </Box>
    );
};

export default DateQuantities;
